import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useUI } from '../contexts/UIProvider';
import type { Opportunity, User } from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import MapPinIcon from './icons/MapPinIcon';
import DollarIcon from './icons/DollarIcon';
import MusicNoteLikeIcon from './icons/MusicNoteLikeIcon';
import CommentIcon from './icons/CommentIcon';
import AmplifyIcon from './icons/AmplifyIcon';
import ShareIcon from './icons/ShareIcon';
import CalendarIcon from './icons/CalendarIcon';
import CloseIcon from './icons/CloseIcon';
import LockClosedIcon from './icons/LockClosedIcon';
import UsersIcon from './icons/UsersIcon';
import { isSameDay } from 'date-fns';

interface OpportunityCardProps {
  opportunity: Opportunity;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity }) => {
  const { currentUser, users } = useAuth();
  const { handleViewOpportunity, handleViewProfile } = useNavigation();
  const { likeOpportunity, amplifyOpportunity } = useOpportunities();
  const { showToast } = useUI();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAmplifierModal, setShowAmplifierModal] = useState(false);
  
  const isLiked = useMemo(() => 
    !!(currentUser && opportunity.likedBy?.includes(currentUser.handle)),
    [currentUser, opportunity.likedBy]
  );
  
  const isAmplified = useMemo(() =>
    !!(currentUser && opportunity.amplifiedBy?.some(amp => amp.handle === currentUser.handle)),
    [currentUser, opportunity.amplifiedBy]
  );

  const isFan = currentUser?.subscriptionTier === 'fan_basic';
  const isAuthorStealth = !!opportunity.author.preferences?.visibility?.stealthMode;

  const formattedDate = useMemo(() => {
    if (!opportunity.startDate) return null;
    const start = new Date(`${opportunity.startDate}T00:00:00`);
    if (!opportunity.endDate || isSameDay(start, new Date(`${opportunity.endDate}T00:00:00`))) {
        return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    const end = new Date(`${opportunity.endDate}T00:00:00`);
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [opportunity.startDate, opportunity.endDate]);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
        const shareUrl = `${window.location.origin}/?v=opp&id=${opportunity.id}`;
        let shareText = `🔥 [NEW ${opportunity.type.toUpperCase()}] on beYourAMP\n\n`;
        shareText += `Title: "${opportunity.title}"\n`;
        shareText += `Posted by: ${opportunity.author.name}\n`;
        shareText += `Location: ${opportunity.location}\n`;
        if (formattedDate) shareText += `Date: ${formattedDate}\n`;
        if (opportunity.salary) shareText += `Pay: ${opportunity.salary.currency}${opportunity.salary.amount.toLocaleString()}\n`;
        shareText += `\nApply here: `;
        
        if (navigator.share) {
            await navigator.share({ title: `beYourAMP | ${opportunity.title}`, text: shareText, url: shareUrl });
        } else {
            await navigator.clipboard.writeText(`${shareText}${shareUrl}`);
            showToast('Link and info copied!');
        }
    } catch (err) { showToast('Error sharing.'); }
  };
  
  const handleUserClick = (e: React.MouseEvent, userToView: Partial<User> & { handle: string; name: string; avatarUrl: string; title: string }) => {
      e.stopPropagation();
      if (isAuthorStealth && userToView.handle === opportunity.author.handle) {
          showToast("Scout Mode active: Profile is restricted.");
          return;
      }
      const foundUser = users.find(u => u.handle === userToView.handle);
      if (foundUser) {
          handleViewProfile(foundUser);
      } else {
          handleViewProfile({ ...userToView, email: '', city: 'Global', handle: userToView.handle, name: userToView.name, avatarUrl: userToView.avatarUrl, title: userToView.title } as User);
      }
  };

  const displayAmplifiers = useMemo(() => {
    if (opportunity.recentAmplifiers && opportunity.recentAmplifiers.length > 0) return opportunity.recentAmplifiers;
    if (opportunity.amplifiedBy && opportunity.amplifiedBy.length > 0) {
      return opportunity.amplifiedBy.slice(0, 3).map(amp => users.find(u => u.handle === amp.handle)).filter(Boolean);
    }
    return [];
  }, [opportunity.recentAmplifiers, opportunity.amplifiedBy, users]);

  const totalAmplifiers = opportunity.amplifies || 0;

  return (
    <div className="bg-brand-surface rounded-lg p-4 sm:p-6 transition-shadow hover:shadow-lg relative border border-gray-800 shadow-sm">
      {displayAmplifiers.length > 0 && (
          <button 
            onClick={(e) => { e.stopPropagation(); setShowAmplifierModal(true); }}
            className="flex items-center space-x-1 text-xs text-brand-muted mb-3 border-b border-gray-800 pb-3 flex-wrap hover:text-brand-orange transition-colors group w-full text-left"
          >
              <AmplifyIcon className="h-3 w-3 mr-1 text-brand-orange" />
              <span className="font-medium">Amplified by </span>
              <strong className="text-gray-300 group-hover:underline ml-1">
                {displayAmplifiers[0]?.name || `@${(opportunity.amplifiedBy?.[0] as any)?.handle}`}
              </strong>
              {totalAmplifiers > 1 && <span className="ml-1 text-brand-muted"> and {totalAmplifiers - 1} others</span>}
          </button>
      )}
      
      <div className="flex items-start space-x-4">
        <img src={opportunity.author.avatarUrl} alt="author" className={`h-12 w-12 rounded-full border border-gray-800 object-cover transition-colors ${isAuthorStealth ? 'cursor-default grayscale' : 'cursor-pointer hover:border-brand-orange'}`} onClick={(e) => handleUserClick(e, opportunity.author)} />
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
                <p className={`font-bold text-white truncate tracking-tight transition-colors ${isAuthorStealth ? 'cursor-default' : 'hover:underline cursor-pointer'}`} onClick={(e) => handleUserClick(e, opportunity.author)}>
                    {opportunity.author.name} {isAuthorStealth && <span className="text-[9px] text-brand-muted italic">(Scout)</span>}
                </p>
                <p className="text-[10px] text-brand-muted font-mono">@{opportunity.author.handle} &middot; {formatTimestamp(opportunity.timestamp)}</p>
            </div>
             <span className="bg-gray-800 text-gray-300 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border border-gray-700">{opportunity.type}</span>
          </div>
          <div className="mt-3 cursor-pointer" onClick={() => handleViewOpportunity(opportunity)}>
            <h3 className="text-xl font-black text-white hover:text-brand-orange transition-colors truncate uppercase tracking-tight">{opportunity.title}</h3>
            <p className={`mt-2 text-sm text-gray-300 leading-relaxed font-medium ${!isExpanded ? 'line-clamp-2' : ''}`} onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>{opportunity.description}</p>
            {opportunity.imageUrl && (
              <img src={opportunity.imageUrl} alt={opportunity.title} className="mt-3 w-full rounded-lg object-cover max-h-72" />
            )}
          </div>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-4 text-[11px] font-bold text-gray-300 uppercase tracking-widest">
            <InfoPill icon={<MapPinIcon className="h-3.5 w-3.5 text-brand-orange" />} text={opportunity.location} />
            {formattedDate && <InfoPill icon={<CalendarIcon className="h-3.5 w-3.5 text-brand-orange" />} text={formattedDate} />}
            {opportunity.salary && <InfoPill icon={<DollarIcon className="h-3.5 w-3.5 text-green-400" />} text={`${opportunity.salary.currency}${opportunity.salary.amount.toLocaleString()} / ${opportunity.salary.period}`} />}
            {opportunity.isOpenCall === false && <InfoPill icon={<LockClosedIcon className="h-3.5 w-3.5 text-yellow-400" />} text="Invite Only" />}
            {opportunity.maxCapacity && <InfoPill icon={<UsersIcon className="h-3.5 w-3.5 text-brand-orange" />} text={`${opportunity.attendeeCount ?? (opportunity.attendees?.length ?? 0)} / ${opportunity.maxCapacity}`} />}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-800 flex justify-between items-center text-brand-muted">
        <div className="flex items-center space-x-2">
            <ActionButton icon={<MusicNoteLikeIcon className="h-5 w-5" />} label="Like" count={opportunity.likes} isActive={isLiked} onClick={(e: React.MouseEvent) => { e.stopPropagation(); likeOpportunity(opportunity.id); }} />
            {!isFan && (
              <>
                <ActionButton icon={<CommentIcon className="h-5 w-5" />} label="Comment" count={opportunity.comments} onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleViewOpportunity(opportunity); }} />
                <ActionButton icon={<AmplifyIcon className="h-5 w-5" />} label="Amplify" count={opportunity.amplifies} isActive={isAmplified} onClick={(e: React.MouseEvent) => { e.stopPropagation(); amplifyOpportunity(opportunity.id); }} />
              </>
            )}
        </div>
        <ActionButton icon={<ShareIcon className="h-5 w-5" />} label="Share" onClick={handleShare} />
      </div>

      {showAmplifierModal && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowAmplifierModal(false)}>
              <div className="bg-brand-surface w-full max-w-sm rounded-2xl border border-gray-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
                      <h4 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                          <AmplifyIcon className="h-4 w-4 text-brand-orange" />
                          Post Amplifiers
                      </h4>
                      <button onClick={() => setShowAmplifierModal(false)} className="p-1 hover:bg-gray-800 rounded-full text-brand-muted transition-colors"><CloseIcon className="h-5 w-5" /></button>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto p-2 custom-scrollbar">
                      {opportunity.amplifiedBy?.map(amp => {
                          const ampUser = users.find(u => u.handle === amp.handle);
                          return (
                              <div 
                                key={amp.handle} 
                                onClick={(e) => { setShowAmplifierModal(false); if (ampUser) handleUserClick(e, ampUser); }}
                                className="flex items-center gap-3 p-3 hover:bg-gray-800/60 rounded-xl cursor-pointer transition-colors"
                              >
                                  <img src={ampUser?.avatarUrl || `https://i.pravatar.cc/150?u=${amp.handle}`} className="h-10 w-10 rounded-full border border-gray-700" alt="" />
                                  <div className="min-w-0">
                                      <p className="text-sm font-bold text-white truncate tracking-tight">{ampUser?.name || amp.handle}</p>
                                      <p className="text-[10px] text-brand-muted font-mono">@{amp.handle}</p>
                                  </div>
                              </div>
                          );
                      })}
                      {(!opportunity.amplifiedBy || opportunity.amplifiedBy.length === 0) && (
                          <p className="p-8 text-center text-brand-muted italic text-sm">No one has amplified this yet.</p>
                      )}
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

const InfoPill: React.FC<{ icon: React.ReactNode, text: string }> = ({ icon, text }) => (
    <div className="flex items-center space-x-1.5 text-brand-muted">
        {icon}<span className="truncate">{text}</span>
    </div>
);

interface ActionButtonProps {
    icon: React.ReactNode;
    label: string;
    count?: number;
    isActive?: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, count, isActive, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled} className={`flex items-center space-x-1.5 transition-all p-2 rounded-lg ${isActive ? 'text-brand-orange bg-brand-orange/10' : 'text-brand-muted'} ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-800/50'}`} aria-label={label}>
        {icon}{count !== undefined && count > 0 && <span className="text-[10px] font-black uppercase tracking-widest">{count}</span>}
    </button>
);

export default OpportunityCard;